import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Award,
  BarChart3,
  Blocks,
  Check,
  ChevronDown,
  FileDown,
  Globe2,
  Layers,
  MousePointerClick,
  Palette,
  PenLine,
  ShieldCheck,
  Sparkles,
  Timer,
  Users,
  Wand2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { CURRENCY_META } from "@/lib/format";
import { CYCLES, cycleMath, detectCurrency, INR_PRICES } from "@/lib/pricing";
import { track } from "@/lib/meta";

const startTrial = (source) =>
  track("StartTrial", { currency: "INR", value: 0, predicted_ltv: 5988, content_name: source });

const AUDIENCE = [
  { icon: Award, title: "Agency owners", copy: "Send 20 branded proposals a week without a designer in the loop." },
  { icon: Sparkles, title: "Founders", copy: "Turn a discovery call into a priced proposal before the follow-up." },
  { icon: BarChart3, title: "Marketers", copy: "See which offers convert, by service line and deal size." },
  { icon: PenLine, title: "Freelancers", copy: "Look like a studio. Get paid faster with online acceptance." },
  { icon: Users, title: "Business owners", copy: "One source of truth for every quote your team sends out." },
];

const FEATURES = [
  { icon: Wand2, title: "AI drafts in 60 seconds", copy: "Answer six questions or paste a meeting transcript. Get a full priced proposal, not a blank page." },
  { icon: Blocks, title: "15 building blocks", copy: "Cover pages, pricing tables, tiers, timelines, case studies, T&Cs and signatures. Drag to reorder." },
  { icon: Palette, title: "Your brand, locked in", copy: "Brand kits with logo, colours, fonts and four cover styles. Freeze blocks so nobody breaks legal copy." },
  { icon: Globe2, title: "Multi-currency & GST", copy: "Lakh-crore or international formatting, CGST/SGST/IGST, per-line discounts. Exact to the paisa." },
  { icon: MousePointerClick, title: "Track every open", copy: "Know when it was opened, how long they read and which sections they actually finished." },
  { icon: PenLine, title: "Close it online", copy: "Clients accept on their phone with a tamper-evident cryptographic fingerprint. No printing, no scanning." },
  { icon: FileDown, title: "PDF & Word export", copy: "Pixel-accurate PDFs with embedded fonts and page numbers, plus branded .docx when they ask for it." },
  { icon: ShieldCheck, title: "Fact-check guard", copy: "Flags any metric or claim the AI could not source and blocks the send until you verify it." },
];

const PLAN_FEATURES = [
  "AI proposal generation (wizard + transcript)",
  "All 15 editor blocks & drag-and-drop",
  "Unlimited brand kits & templates",
  "Client CRM with website auto-fill",
  "Multi-currency + GST engine",
  "Secure share links with password & expiry",
  "Open, time-spent & section tracking",
  "Online acceptance with e-signature",
  "PDF & Word export",
  "Analytics dashboard with funnel & CSV",
];

const STATS = [
  { value: "30s", label: "First draft" },
  { value: "15", label: "Block types" },
  { value: "7", label: "Currencies" },
  { value: "0", label: "Client logins" },
];

const FAQ = [
  { q: "What counts as one proposal?", a: "Every proposal you create counts once, no matter how many times you edit, export or resend it. Bonus proposals from quarterly and yearly plans sit in the same pool." },
  { q: "Do unused proposals carry over?", a: "Your quarterly and yearly allowance is pooled for the whole term, so a quiet month simply leaves more for a busy one." },
  { q: "Can my clients see the pricing tiers?", a: "Yes. Add a tiered pricing block and your client picks Good, Better or Best right inside the proposal before accepting." },
  { q: "Which currency am I billed in?", a: "We detect your region automatically and you can switch currency yourself at any time from the toggle above." },
  { q: "Can I bring my own AI key?", a: "Yes. Propojo ships with AI included, and you can add your own OpenRouter key in Settings to pick any model in the live catalogue." },
];

const Nav = () => {
  const { user } = useAuth();
  return (
  <header className="sticky top-0 z-50 nv-glass border-b border-zinc-800/70">
    <div className="mx-auto max-w-6xl px-5 h-16 flex items-center gap-3">
      <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
        <Logo size={28} testid="landing-logo-mark" />
        <span className="font-display font-extrabold tracking-tight">Propojo</span>
      </Link>
      <nav className="hidden md:flex items-center gap-6 ml-8 text-sm text-zinc-400">
        <a href="#features" className="hover:text-zinc-100 transition-colors">Features</a>
        <a href="#who" className="hover:text-zinc-100 transition-colors">Who it's for</a>
        <a href="#how" className="hover:text-zinc-100 transition-colors">How it works</a>
        <a href="#templates" className="hover:text-zinc-100 transition-colors">Templates</a>
        <a href="#pricing" className="hover:text-zinc-100 transition-colors">Pricing</a>
        <a href="#faq" className="hover:text-zinc-100 transition-colors">FAQ</a>
      </nav>
      <div className="ml-auto flex items-center gap-2">
        {user ? (
          <Link to="/dashboard">
            <Button data-testid="landing-dashboard-btn" className="h-9 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg">
              Go to dashboard <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </Link>
        ) : (
          <>
            <Link to="/login">
              <Button data-testid="landing-signin-btn" variant="ghost" className="h-9 text-zinc-300 hover:text-white">Sign in</Button>
            </Link>
            <Link to="/signup">
              <Button data-testid="landing-nav-cta-btn" onClick={() => startTrial("nav")} className="h-9 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg">
                Start free
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  </header>
  );
};

const DashboardMock = () => (
  <div className="relative">
    <div className="absolute -inset-8 bg-indigo-500/12 blur-3xl rounded-full" />
    <div className="relative nv-card p-4 sm:p-5 shadow-2xl overflow-hidden">
      <div className="flex items-center gap-2 mb-4">
        <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
        <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
        <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
        <span className="ml-2 text-[10px] font-num uppercase tracking-[0.18em] text-zinc-600">propojo · analytics</span>
      </div>
      <div className="grid grid-cols-3 gap-2.5">
        {[
          ["Sent", "48"],
          ["Acceptance", "62%"],
          ["Closed", "₹41.2 L"],
        ].map(([l, v]) => (
          <div key={l} className="rounded-lg border border-zinc-800/80 bg-zinc-900/50 p-3">
            <p className="text-[9px] font-num uppercase tracking-[0.16em] text-zinc-500">{l}</p>
            <p className="text-lg font-bold font-num mt-1">{v}</p>
          </div>
        ))}
      </div>
      <div className="mt-3 rounded-lg border border-zinc-800/80 bg-zinc-900/50 p-3.5">
        <p className="text-[9px] font-num uppercase tracking-[0.16em] text-zinc-500 mb-2.5">Conversion funnel</p>
        {[
          ["Created", 100, "#6366F1"],
          ["Sent", 82, "#3B82F6"],
          ["Opened", 61, "#F59E0B"],
          ["Accepted", 38, "#10B981"],
        ].map(([label, pct, color], i) => (
          <div key={label} className="mb-2 last:mb-0">
            <div className="flex justify-between text-[10px] text-zinc-400 mb-1">
              <span>{label}</span>
              <span className="font-num">{pct}%</span>
            </div>
            <div className="h-4 rounded bg-zinc-800/60 overflow-hidden">
              <div
                className="h-full rounded"
                style={{ width: `${pct}%`, background: color, animation: `nvRise .8s ${i * 0.12}s both` }}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2.5">
        {[
          ["Retainer — Acme", "accepted", "text-emerald-400"],
          ["SEO — Northwind", "viewed", "text-amber-400"],
        ].map(([t, s, c]) => (
          <div key={t} className="rounded-lg border border-zinc-800/80 bg-zinc-900/50 p-3">
            <p className="text-xs text-zinc-300 truncate">{t}</p>
            <p className={`text-[10px] font-num uppercase tracking-wide mt-1 ${c}`}>{s}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default function Landing() {
  const [currency, setCurrency] = useState("USD");
  const [detected, setDetected] = useState("USD");
  const [cycle, setCycle] = useState("yearly");
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    const found = detectCurrency();
    setDetected(found);
    setCurrency(found);
  }, []);

  const math = cycleMath(cycle, currency);
  const active = math.cycle;

  return (
    <div className="min-h-screen bg-app text-zinc-100">
      <Nav />

      {/* Hero */}
      <section className="nv-grid-bg border-b border-zinc-800/60">
        <div className="mx-auto max-w-6xl px-5 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-14 items-center">
          <div className="nv-rise">
            <span className="inline-flex items-center gap-2 rounded-full border border-indigo-500/25 bg-indigo-500/10 px-3 py-1 text-[11px] font-num uppercase tracking-[0.16em] text-indigo-300">
              <Timer className="w-3 h-3" /> Proposal to signature in one sitting
            </span>            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Build Your Proposal
              <br />
              <span className="text-indigo-400">in 30 Seconds</span>
            </h1>
            <p className="mt-5 text-base md:text-lg text-zinc-400 max-w-xl leading-relaxed">
              Create professional, industry-standard proposals faster and close deals sooner. Propojo writes the
              draft, wraps it in your brand, and lets clients accept it on their phone.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/signup">
                <Button data-testid="hero-primary-cta-btn" onClick={() => startTrial("hero")} className="h-12 px-6 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl text-base shadow-[0_10px_40px_-12px_rgba(99,102,241,0.85)]">
                  Start free <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
              <a href="#pricing">
                <Button data-testid="hero-secondary-cta-btn" variant="outline" className="h-12 px-6 border-zinc-800 text-zinc-300 hover:bg-zinc-800/60 rounded-xl text-base">
                  See pricing
                </Button>
              </a>
            </div>
            <p className="mt-4 text-xs text-zinc-500">No card to start · Your clients never need a login</p>

            <div className="mt-10 grid grid-cols-4 gap-4 max-w-lg">
              {STATS.map((s) => (
                <div key={s.label}>
                  <p className="text-xl sm:text-2xl font-bold font-num text-zinc-100">{s.value}</p>
                  <p className="text-[10px] font-num uppercase tracking-[0.14em] text-zinc-500 mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="nv-rise" style={{ animationDelay: "0.12s" }}>
            <DashboardMock />
          </div>
        </div>
      </section>

      {/* Audience */}
      <section id="who" className="mx-auto max-w-6xl px-5 py-20">
        <p className="text-[11px] font-num uppercase tracking-[0.22em] text-indigo-400">Built for people who sell</p>
        <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">
          If a proposal stands between you and the money, this is for you.
        </h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {AUDIENCE.map(({ icon: Icon, title, copy }) => (
            <div key={title} data-testid={`audience-card-${title.toLowerCase().replace(/\s+/g, "-")}`} className="nv-card p-5 hover:-translate-y-0.5 transition-transform duration-200">
              <Icon className="w-5 h-5 text-indigo-400" />
              <p className="mt-3 font-semibold">{title}</p>
              <p className="mt-1.5 text-sm text-zinc-500 leading-relaxed">{copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-y border-zinc-800/60 bg-app">
        <div className="mx-auto max-w-6xl px-5 py-20">
          <p className="text-[11px] font-num uppercase tracking-[0.22em] text-indigo-400">Everything in one place</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">
            A proposal desk, not another document editor.
          </h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {FEATURES.map(({ icon: Icon, title, copy }) => (
              <div key={title} className="nv-card p-5 group">
                <div className="w-9 h-9 rounded-lg bg-indigo-500/12 border border-indigo-500/25 grid place-items-center group-hover:bg-indigo-500/20 transition-colors">
                  <Icon className="w-4 h-4 text-indigo-400" />
                </div>
                <p className="mt-3.5 font-semibold">{title}</p>
                <p className="mt-1.5 text-sm text-zinc-500 leading-relaxed">{copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="mx-auto max-w-6xl px-5 py-20">
        <p className="text-[11px] font-num uppercase tracking-[0.22em] text-indigo-400">How it works</p>
        <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">Three steps from call to signature.</h2>
        <div className="mt-10 grid md:grid-cols-3 gap-4">
          {[
            { n: "01", t: "Brief it", c: "Answer six quick questions or paste your meeting transcript. Propojo extracts the scope, pricing and timeline." },
            { n: "02", t: "Polish it", c: "Edit any block, rewrite a section with one click, and apply your brand kit. Live preview the whole time." },
            { n: "03", t: "Send & close", c: "Share a secure link, watch them read it, and get an e-signed acceptance with a tamper-evident fingerprint." },
          ].map((s) => (
            <div key={s.n} data-testid={`how-step-${s.n}`} className="nv-card p-6">
              <span className="text-3xl font-num font-bold text-indigo-500/40">{s.n}</span>
              <p className="mt-3 text-lg font-semibold">{s.t}</p>
              <p className="mt-1.5 text-sm text-zinc-500 leading-relaxed">{s.c}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Templates */}
      <section id="templates" className="border-y border-zinc-800/60 bg-app">
        <div className="mx-auto max-w-6xl px-5 py-20">
          <p className="text-[11px] font-num uppercase tracking-[0.22em] text-indigo-400">Templates</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">Start from a structure that already wins.</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { t: "Marketing Retainer", c: "Monthly growth partnership with tiered pricing and reporting cadence.", tag: "Marketing" },
              { t: "Web Development", c: "Phased build with fixed pricing and a clear out-of-scope list.", tag: "Development" },
              { t: "SEO Engagement", c: "Technical plus content programme with milestone reporting.", tag: "Marketing" },
              { t: "Consulting Sprint", c: "Executive advisory framing the problem and the decision it unlocks.", tag: "Consulting" },
            ].map((tpl) => (
              <div key={tpl.t} data-testid={`template-preview-${tpl.tag.toLowerCase()}`} className="nv-card p-5">
                <span className="text-[10px] font-num uppercase tracking-[0.16em] text-indigo-400">{tpl.tag}</span>
                <p className="mt-2.5 font-semibold">{tpl.t}</p>
                <p className="mt-1.5 text-sm text-zinc-500 leading-relaxed">{tpl.c}</p>
                <div className="mt-4 space-y-1.5">
                  {[80, 62, 45].map((w, i) => (
                    <div key={i} className="h-1.5 rounded-full bg-zinc-800" style={{ width: `${w}%` }} />
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-zinc-500">Plus your own templates with placeholder fields and custom AI instructions.</p>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="mx-auto max-w-6xl px-5 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-[11px] font-num uppercase tracking-[0.22em] text-indigo-400">Pricing</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight">One plan. Every feature. Pick your term.</h2>
          <p className="mt-3 text-sm md:text-base text-zinc-400">
            Longer terms get bonus proposals and a lower effective monthly price. Nothing is feature-gated.
          </p>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <div data-testid="billing-cycle-toggle" className="inline-flex rounded-xl border border-zinc-800 bg-panel p-1">
            {CYCLES.map((c) => (
              <button
                key={c.key}
                data-testid={`cycle-${c.key}-btn`}
                onClick={() => setCycle(c.key)}
                className={`relative px-4 sm:px-5 h-10 rounded-lg text-sm transition-colors duration-200 ${
                  cycle === c.key ? "bg-indigo-500 text-white shadow-[0_6px_24px_-10px_rgba(99,102,241,0.9)]" : "text-zinc-400 hover:text-zinc-200"
                }`}
              >
                {c.label}
                {c.save > 0 && (
                  <span className={`ml-1.5 text-[10px] font-num ${cycle === c.key ? "text-white/80" : "text-emerald-400"}`}>
                    -{c.save}%
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <select
              data-testid="currency-select"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="h-10 rounded-xl border border-zinc-800 bg-panel px-3 text-sm text-zinc-300 font-num"
            >
              {Object.keys(CURRENCY_META).map((c) => (
                <option key={c} value={c}>
                  {c} {CURRENCY_META[c].symbol.trim()}
                </option>
              ))}
            </select>
            {currency === detected && (
              <span data-testid="currency-autodetect-note" className="text-[11px] text-zinc-500">auto-detected</span>
            )}
          </div>
        </div>

        <div className="mt-10 grid lg:grid-cols-5 gap-4 items-start">
          <div data-testid="pricing-card" className="lg:col-span-3 relative nv-card p-6 sm:p-8 border-indigo-500/30">
            {active.popular && (
              <span data-testid="most-popular-badge" className="absolute -top-3 left-6 sm:left-8 rounded-full bg-indigo-500 px-3 py-1 text-[10px] font-num uppercase tracking-[0.16em] text-white shadow-lg">
                Most popular
              </span>
            )}
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-num uppercase tracking-[0.18em] text-zinc-500">Propojo Pro · {active.label}</p>
                <div className="mt-3 flex items-end gap-2">
                  <span data-testid="plan-price" className="text-4xl sm:text-5xl font-extrabold font-num tracking-tight">
                    {math.totalLabel}
                  </span>
                  <span className="text-sm text-zinc-500 mb-1.5">
                    /{active.months === 1 ? "month" : active.months === 3 ? "quarter" : "year"}
                  </span>
                </div>
                <p data-testid="plan-effective-monthly" className="mt-1.5 text-sm text-zinc-400">
                  {math.perMonthLabel} per month effective
                  {math.hasSaving && (
                    <>
                      {" · "}
                      <span className="line-through text-zinc-600 font-num">{math.listLabel}</span>{" "}
                      <span className="text-emerald-400 font-num">save {math.savedLabel}</span>
                    </>
                  )}
                </p>
              </div>

              <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 px-4 py-3 min-w-[168px]">
                <p className="text-[10px] font-num uppercase tracking-[0.16em] text-zinc-500">Proposal credits</p>
                <p data-testid="plan-proposal-limit" className="text-2xl font-bold font-num mt-1">{active.proposals}</p>
                <p className="text-[11px] text-zinc-500">{active.note}</p>
                {active.bonus > 0 && (
                  <p className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-emerald-500/12 border border-emerald-500/25 px-2 py-0.5 text-[10px] font-num text-emerald-400">
                    <Layers className="w-3 h-3" /> +{active.bonus} bonus
                  </p>
                )}
              </div>
            </div>

            <div className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
              {PLAN_FEATURES.map((f) => (
                <div key={f} className="flex items-start gap-2.5">
                  <span className="mt-0.5 w-4 h-4 rounded-full bg-emerald-500/15 border border-emerald-500/30 grid place-items-center shrink-0">
                    <Check className="w-2.5 h-2.5 text-emerald-400" />
                  </span>
                  <span className="text-sm text-zinc-300">{f}</span>
                </div>
              ))}
            </div>

            <Link to="/signup" onClick={() => sessionStorage.setItem("pj_plan", cycle)}>
              <Button data-testid="pricing-get-started-btn" className="mt-8 w-full h-12 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl text-base">
                Get started <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
            <p className="mt-3 text-center text-[11px] text-zinc-500">
              Shown in {currency}. All plans are charged in INR ({math.inrLabel}) — UPI, cards, wallets or netbanking, on a secure PCI-DSS compliant gateway.
            </p>
          </div>

          <div className="lg:col-span-2 space-y-3">
            {CYCLES.map((c) => {
              const m = cycleMath(c.key, currency);
              const isActive = cycle === c.key;
              return (
                <button
                  key={c.key}
                  data-testid={`cycle-summary-${c.key}`}
                  onClick={() => setCycle(c.key)}
                  className={`w-full text-left nv-card p-4 flex items-center gap-3 transition-colors ${
                    isActive ? "border-indigo-500/40 bg-indigo-500/[0.07]" : ""
                  }`}
                >
                  <span
                    className={`w-4 h-4 rounded-full border grid place-items-center shrink-0 ${
                      isActive ? "border-indigo-400" : "border-zinc-700"
                    }`}
                  >
                    {isActive && <span className="w-2 h-2 rounded-full bg-indigo-400" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="text-sm font-medium text-zinc-200">{c.label}</span>
                      {c.popular && <span className="text-[9px] font-num uppercase tracking-wide text-indigo-300">popular</span>}
                    </span>
                    <span className="block text-[11px] text-zinc-500">{c.proposals} proposals · {c.note}</span>
                  </span>
                  <span className="text-right shrink-0">
                    <span className="block text-sm font-num text-zinc-100">{m.totalLabel}</span>
                    {c.save > 0 && <span className="block text-[10px] font-num text-emerald-400">save {c.save}%</span>}
                  </span>
                </button>
              );
            })}
            <div className="nv-card p-4">
              <p className="text-sm font-medium text-zinc-200">Need more seats or a white label?</p>
              <p className="mt-1 text-xs text-zinc-500">
                Resellers and larger teams get pooled credits and custom seat counts. Start free and we'll sort it out.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="mx-auto max-w-6xl px-5 pb-4">
        <p className="text-[11px] font-num uppercase tracking-[0.22em] text-indigo-400">What teams say</p>
        <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">Built with the people sending proposals every week.</h2>
        <div className="mt-10 grid md:grid-cols-3 gap-4">
          {[
            { q: "I used to lose a full evening to every proposal. Now the draft is ready before I've finished my notes.", n: "Aditya R.", r: "Founder, performance agency" },
            { q: "The tracking is the bit clients don't expect. I know they read the pricing page twice before I call.", n: "Meera S.", r: "Marketing consultant" },
            { q: "Branded PDFs and online acceptance made us look twice our size. Deals close in days, not weeks.", n: "Karan T.", r: "Freelance designer" },
          ].map((t) => (
            <figure key={t.n} data-testid={`testimonial-${t.n.split(" ")[0].toLowerCase()}`} className="nv-card p-6">
              <blockquote className="text-sm text-zinc-300 leading-relaxed">“{t.q}”</blockquote>
              <figcaption className="mt-4 flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-full bg-indigo-500/15 border border-indigo-500/25 grid place-items-center text-[11px] font-num text-indigo-300">
                  {t.n.slice(0, 1)}
                </span>
                <span>
                  <span className="block text-sm text-zinc-200">{t.n}</span>
                  <span className="block text-[11px] text-zinc-500">{t.r}</span>
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
        <p className="mt-4 text-[11px] text-zinc-600">Illustrative quotes from our early access group.</p>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-t border-zinc-800/60 bg-app">
        <div className="mx-auto max-w-3xl px-5 py-20">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Questions, answered</h2>
          <div className="mt-8 divide-y divide-zinc-800/70">
            {FAQ.map((item, i) => (
              <div key={item.q} className="py-4">
                <button
                  data-testid={`faq-toggle-${i}`}
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 text-left"
                >
                  <span className="text-base text-zinc-100">{item.q}</span>
                  <ChevronDown className={`w-4 h-4 text-zinc-500 shrink-0 transition-transform duration-200 ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <p className="mt-2.5 text-sm text-zinc-400 leading-relaxed nv-rise">{item.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="mx-auto max-w-6xl px-5 py-20">
        <div className="rounded-2xl border border-indigo-500/25 bg-indigo-500/[0.07] p-8 sm:p-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Your next proposal can go out today.</h2>
          <p className="mt-3 text-sm md:text-base text-zinc-400 max-w-xl mx-auto">
            Set up your brand kit once, then let AI and your templates do the typing.
          </p>
          <Link to="/signup">
            <Button data-testid="footer-cta-btn" onClick={() => startTrial("footer")} className="mt-7 h-12 px-7 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl text-base">
              Start free <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-zinc-800/60">
        <div className="mx-auto max-w-6xl px-5 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-zinc-500">
          <div className="flex items-center gap-2">
            <Logo size={22} />
            <span>© {new Date().getFullYear()} Propojo · Proposal OS</span>
          </div>
          <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2" data-testid="landing-footer-nav">
            <Link to="/about" className="hover:text-zinc-300 transition-colors" data-testid="landing-footer-about">About us</Link>
            <Link to="/contact" className="hover:text-zinc-300 transition-colors" data-testid="landing-footer-contact">Contact</Link>
            <Link to="/privacy" className="hover:text-zinc-300 transition-colors" data-testid="landing-footer-privacy">Privacy</Link>
            <Link to="/terms" className="hover:text-zinc-300 transition-colors" data-testid="landing-footer-terms">Terms</Link>
            <Link to="/refund-policy" className="hover:text-zinc-300 transition-colors" data-testid="landing-footer-refund">Refunds</Link>
            <Link to="/login" className="hover:text-zinc-300 transition-colors">Sign in</Link>
            <Link to="/signup" className="hover:text-zinc-300 transition-colors">Create workspace</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
